import { jsx as _jsx, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
import { css } from '@emotion/react';
import { between, from, until, textSans, space, } from '@guardian/source-foundations';
import { ArticleDisplay, ArticleDesign, ArticleSpecial, } from '@guardian/libs';
import CameraSvg from '../../static/icons/camera.svg';
import VideoSvg from '../../static/icons/video-icon.svg';
const captionStyle = (palette) => css `
	${textSans.xsmall()};
	line-height: 135%;
	padding-top: 6px;
	word-wrap: break-all;
	color: ${palette.text.caption};
`;
const bottomMargin = css `
	margin-bottom: 6px;
`;
const overlayedBottomPadding = (format) => {
    if (format.display === ArticleDisplay.Showcase &&
        format.design === ArticleDesign.Review) {
        return css `
			padding-bottom: 2.5rem;
		`;
    }
    return css `
		padding-bottom: 0.375rem;
	`;
};
const overlayedStyles = (palette, format) => css `
	position: absolute;
	left: 0;
	right: 0;
	bottom: 0;
	background: rgba(18, 18, 18, 0.8);

	span {
		color: ${palette.text.overlayedCaption};
		font-size: 0.75rem;
		line-height: 1rem;
	}

	svg {
		fill: ${palette.text.overlayedCaption};
	}
	color: ${palette.text.overlayedCaption};
	font-size: 0.75rem;
	line-height: 1rem;
	padding-top: 0.375rem;
	padding-right: 2.5rem;
	padding-left: 0.75rem;
	${overlayedBottomPadding(format)};

	flex-grow: 1;
	min-height: 2.25rem;
`;
const limitedWidth = css `
	${from.leftCol} {
		width: 140px;
		/* use absolute position here to allow the article text to push up alongside
           the caption when it is limited in width */
		position: absolute;
	}
	${from.wide} {
		width: 220px;
	}
`;
const veryLimitedWidth = css `
	${from.leftCol} {
		width: 120px;
		/* use absolute position here to allow the article text to push up alongside
           the caption when it is limited in width */
		position: absolute;
	}
	${from.wide} {
		width: 184px;
	}
`;
const captionPadding = css `
	padding-left: 8px;
	padding-right: 8px;
`;
const videoPadding = css `
	${until.mobileLandscape} {
		margin-left: 10px;
	}
	${between.mobileLandscape.and.phablet} {
		margin-left: ${space[5]}px;
	}
`;
const bigLeftMargin = css `
	width: inherit;
	margin-left: ${space[9]}px;
	${until.wide} {
		margin-left: 20px;
		margin-right: 20px;
	}
	${until.mobileLandscape} {
		margin-left: 10px;
		margin-right: 10px;
	}
`;
const hideIconBelowLeftCol = css `
	${until.leftCol} {
		display: none;
	}
`;
const pictureRatio = (13 / 18) * 100;
const videoRatio = (23 / 36) * 100;
const iconStyle = (palette) => css `
	fill: ${palette.fill.cameraCaptionIcon};
	margin-right: ${space[1]}px;
	display: inline-block;
	position: relative;
	width: 1em;
	vertical-align: baseline;
	::before {
		content: ' ';
		display: block;
		padding-top: ${pictureRatio}%;
	}
	svg {
		top: 0px;
		right: 0px;
		bottom: 0px;
		left: 0px;
		width: 100%;
		position: absolute;
		height: 100%;
	}
`;
const videoIconStyle = css `
	width: 1.1em;
	::before {
		padding-top: ${videoRatio}%;
	}
`;
const captionLink = (palette) => css `
	a {
		color: ${palette.text.captionLink};
		text-decoration: none;
	}
	a:hover {
		text-decoration: underline;
	}
	strong {
		font-weight: bold;
	}
`;
const CameraIcon = ({ palette, format }) => {
    return (_jsx("span", Object.assign({ css: [
            iconStyle(palette),
            format.display === ArticleDisplay.Immersive &&
                hideIconBelowLeftCol,
        ] }, { children: _jsx(CameraSvg, {}, void 0) }), void 0));
};
const VideoIcon = ({ palette, format }) => {
    return (_jsx("span", Object.assign({ css: [
            iconStyle(palette),
            format.display === ArticleDisplay.Immersive &&
                hideIconBelowLeftCol,
            videoIconStyle,
        ] }, { children: _jsx(VideoSvg, {}, void 0) }), void 0));
};
export const Caption = ({ captionText, format, palette, padCaption = false, credit, displayCredit = true, shouldLimitWidth = false, isOverlayed, isLeftCol, mediaType = 'Gallery', }) => {
    // Sometimes captions come thorough as a single blank space, so we trim here to ignore those
    const noCaption = !captionText?.trim();
    const noCredit = !credit;
    const hideCredit = !displayCredit;
    if (noCaption && (noCredit || hideCredit))
        return null;
    const defaultCaption = (_jsxs("figcaption", Object.assign({ css: [
            captionStyle(palette),
            shouldLimitWidth && limitedWidth,
            !isOverlayed && bottomMargin,
            isOverlayed && overlayedStyles(palette, format),
            padCaption && captionPadding,
            mediaType === 'Video' && videoPadding,
        ] }, { children: [mediaType === 'Video' ? (_jsx(VideoIcon, { palette: palette, format: format }, void 0)) : (_jsx(CameraIcon, { palette: palette, format: format }, void 0)), captionText && (_jsx("span", { css: captionLink(palette), 
                // eslint-disable-next-line react/no-danger
                dangerouslySetInnerHTML: {
                    __html: captionText || '',
                } }, "caption")), credit && displayCredit && ` ${credit}`] }), void 0));
    switch (format.design) {
        case ArticleDesign.PhotoEssay:
            if (format.theme === ArticleSpecial.Labs && isLeftCol) {
                return defaultCaption;
            }
            return (_jsxs("figcaption", Object.assign({ css: [
                    css `
							${textSans.xxsmall({ lineHeight: 'tight' })};
							color: ${palette.text.caption};
							width: 100%;
							margin-top: ${space[3]}px;
							li:not(:first-child) {
								margin-top: ${space[3]}px;
							}
							li {
								padding-top: ${space[2]}px;
								border-top: 1px solid ${palette.text.caption};
							}
						`,
                    bottomMargin,
                    padCaption && captionPadding,
                    shouldLimitWidth && veryLimitedWidth,
                    shouldLimitWidth && bigLeftMargin,
                ] }, { children: [captionText && (_jsx("span", { css: captionLink(palette), 
                        // eslint-disable-next-line react/no-danger
                        dangerouslySetInnerHTML: {
                            __html: captionText || '',
                        } }, "caption")), credit && displayCredit && ` ${credit}`] }), void 0));
        default:
            return defaultCaption;
    }
};
